<template>
  <div class="card-ticket-form-wrapper">
    <!-- Card 1: Ticket Details -->
    <div class="card-ticket-form">
      <div class="card-header">
        <h3 class="card-title">Ticket Details</h3>
      </div>

      <div class="card-body">
        <div class="form-grid">

          <div class="form-row">
            <label class="form-label">Patient</label>
            <div class="form-field">
              <div class="form-editor" v-if="createMode">
                <PatientSelectInput :modelValue="modelValue.patientId"
                  @update:modelValue="updateField('patientId', $event)" placeholder="Select patient" />
              </div>
              <div v-else class="form-display">
                <router-link v-if="modelValue.patientId" :to="`/patients/${modelValue.patientId}`" class="entity-link">
                  <IconCircle :initials="getPatientInitials()" :size="22" bgColor="var(--icon-patient-bg)" color="var(--icon-patient-color)" />
                  <span class="entity-name">{{ getPatientName() }}</span>
                </router-link>
                <span v-else class="no-data">No data</span>
              </div>
            </div>
          </div>

          <div class="form-row">
            <label class="form-label">Sales Counselor</label>
            <div class="form-field">
              <div class="form-editor" v-if="createMode || editMode">
                <UserSelectInput :modelValue="modelValue.counselorId"
                  @update:modelValue="updateField('counselorId', $event)" placeholder="Select sales counselor" />
              </div>
              <div v-else class="form-display">
                <div v-if="modelValue.counselorId" class="entity-display">
                  <span class="entity-name">{{ getCounselorName() }}</span>
                </div>
                <span v-else class="no-data">No data</span>
              </div>
            </div>
          </div>

          <div class="form-row">
            <label class="form-label">Ticket Date</label>
            <div class="form-field">
              <div class="form-editor" v-if="createMode || editMode">
                <DateInput :modelValue="modelValue.dateTicketStart"
                  @update:modelValue="updateField('dateTicketStart', $event)" placeholder="MM/DD/YYYY" />
              </div>
              <div v-else class="form-display">
                <span :class="{ 'no-data': !modelValue.dateTicketStart }">
                  {{ formatDate(modelValue.dateTicketStart) || 'No data' }}
                </span>
              </div>
            </div>
          </div>

          <div class="form-row">
            <label class="form-label">Ticket Items</label>
            <div class="form-field" style="display: flex;">
              <div class="ticket-items-container">
                <table class="items-table">
                  <thead>
                    <tr>
                      <th class="col-product">Product</th>
                      <th class="col-duration">Duration</th>
                      <th class="col-quantity">Quantity</th>
                      <th class="col-price">Total Price</th>
                      <th class="col-action"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, index) in displayItems" :key="getItemId(item, index)">
                      <td class="col-product">
                        <ProductSelectInput v-if="createMode" :modelValue="item.productId"
                          @update:modelValue="handleProductSelect(index, $event)" />
                        <ProductSelectInput v-else-if="editMode && item.productId" :modelValue="item.productId"
                          @update:modelValue="handleProductSelectEdit(index, $event)" />
                        <div v-else-if="item.productId" class="entity-display">
                          <span class="entity-name">{{ getProductName(item) }}</span>
                        </div>
                        <span v-else class="no-data">N/A</span>
                      </td>
                      <td class="col-duration">
                        <SelectInput v-if="createMode && item.productId" :modelValue="item.durationMonths"
                          @update:modelValue="updateItem(index, 'durationMonths', $event)"
                          :options="durationOptions" placeholder="Select..." />
                        <SelectInput v-else-if="editMode && item.productId" :modelValue="item.durationMonths"
                          @update:modelValue="updateItemSimple(index, 'durationMonths', $event)"
                          :options="durationOptions" placeholder="Select..." />
                        <span v-else>{{ item.productId ? formatDuration(item.durationMonths) : '' }}</span>
                      </td>
                      <td class="col-quantity">
                        <NumericalInput v-if="createMode && item.productId" :modelValue="item.quantity"
                          @update:modelValue="updateItem(index, 'quantity', $event)" />
                        <NumericalInput v-else-if="editMode && item.productId" :modelValue="item.quantity"
                          @update:modelValue="updateItemSimple(index, 'quantity', $event)" />
                        <span v-else>{{ item.quantity || '' }}</span>
                      </td>
                      <td class="col-price">
                        <MonetaryInput v-if="createMode && item.productId" :modelValue="item.amountCents"
                          @update:modelValue="updateItem(index, 'amountCents', $event)" />
                        <div v-else-if="editMode && item.productId" class="locked-field" title="Total cost cannot be changed">
                          <span>{{ formatCurrency(item.amountCents) }}</span>
                          <LockIcon :size="14" class="lock-icon" />
                        </div>
                        <span v-else-if="item.productId">{{ formatCurrency(item.amountCents) }}</span>
                        <span v-else></span>
                      </td>
                      <td class="col-action">
                        <button v-if="createMode && item.productId" @click="removeItem(index)" class="button icon-btn">
                          <DeleteIcon :size="18" />
                        </button>
                      </td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr class="total-row">
                      <td colspan="3" class="total-label">Total:</td>
                      <td class="total-amount">{{ formatCurrency(amountTotal) }}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>

          <div class="form-row">
            <label class="form-label">Initial Payment</label>
            <div class="form-field">
              <div class="form-editor" v-if="createMode">
                <MonetaryInput :modelValue="modelValue.amountDueToday"
                  @update:modelValue="handleInitialPaymentChange" />
              </div>
              <div v-else class="form-display">
                <span>{{ formatCurrency(modelValue.amountDueToday) }}</span>
              </div>
            </div>
          </div>

          <div class="form-row">
            <label class="form-label">Payment Plan Amount</label>
            <div class="form-field">
              <div class="form-editor" v-if="createMode">
                <MonetaryInput :modelValue="paymentPlanAmount"
                  @update:modelValue="handlePaymentPlanAmountChange" />
              </div>
              <div v-else class="form-display">
                <span>{{ formatCurrency(paymentPlanAmount) }}</span>
              </div>
            </div>
          </div>

          <div class="form-row">
            <label class="form-label">Payment Plan Term</label>
            <div class="form-field">
              <div class="form-editor" v-if="createMode">
                <SelectInput :modelValue="modelValue.paymentPlanMonths"
                  @update:modelValue="updateField('paymentPlanMonths', $event)" :options="paymentPlanOptions"
                  placeholder="Select payment plan term" />
              </div>
              <div v-else class="form-display">
                <span>{{ formatPaymentPlan(modelValue.paymentPlanMonths) }}</span>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>

    <!-- Card 2: Payment Summary (only in view mode) -->
    <div v-if="!createMode" class="card-ticket-form">
      <div class="card-header">
        <h3 class="card-title">Payment Summary</h3>
      </div>

      <div class="card-body">
        <div class="form-grid">
          <div class="form-row">
            <label class="form-label">Status</label>
            <div class="form-field">
              <div class="form-display">
                <StatusBadge :status="ticketStatus" />
              </div>
            </div>
          </div>

          <div class="form-row">
            <label class="form-label">Amount Paid</label>
            <div class="form-field">
              <div class="form-display">
                <span>{{ formatCurrency(modelValue.amountCentsPaid) }}</span>
              </div>
            </div>
          </div>

          <div class="form-row">
            <label class="form-label">Amount Refunded</label>
            <div class="form-field">
              <div class="form-display">
                <span>{{ formatCurrency(modelValue.amountCentsRefunded) }}</span>
              </div>
            </div>
          </div>

          <div class="form-row">
            <label class="form-label">Amount Collected</label>
            <div class="form-field">
              <div class="form-display">
                <span>{{ formatCurrency(modelValue.amountCentsCollected) }}</span>
              </div>
            </div>
          </div>

          <div class="form-row">
            <label class="form-label">Amount Cancelled</label>
            <div class="form-field">
              <div class="form-display">
                <span>{{ formatCurrency(modelValue.amountCentsCancelled) }}</span>
              </div>
            </div>
          </div>

          <div class="form-row">
            <label class="form-label">Current Balance</label>
            <div class="form-field">
              <div class="form-display">
                <span class="balance-value">{{ formatCurrency(modelValue.amountCentsBalance) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DateInput from '../inputs/DateInput.vue';
import PatientSelectInput from '../inputs/PatientSelectInput.vue';
import UserSelectInput from '../inputs/UserSelectInput.vue';
import ProductSelectInput from '../inputs/ProductSelectInput.vue';
import NumericalInput from '../inputs/NumericalInput.vue';
import MonetaryInput from '../inputs/MonetaryInput.vue';
import SelectInput from '../inputs/SelectInput.vue';
import IconCircle from '../common/IconCircle.vue';
import StatusBadge from '../common/StatusBadge.vue';
import DeleteIcon from 'vue-material-design-icons/Delete.vue';
import LockIcon from 'vue-material-design-icons/Lock.vue';
import * as formatUtils from '@/utils/formatters';
import { calculateTicketStatus } from '@/utils/formatters';
import { addMonths } from '@/utils/dateUtils';
import * as api from '@/api';
import { queryClient } from '@/libs/queryClient';

export default {
  name: 'CardTicketForm',
  props: {
    modelValue: {
      type: Object,
      required: true
    },
    createMode: {
      type: Boolean,
      default: false
    },
    editMode: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue'],
  components: {
    DateInput,
    PatientSelectInput,
    UserSelectInput,
    ProductSelectInput,
    NumericalInput,
    MonetaryInput,
    SelectInput,
    IconCircle,
    StatusBadge,
    DeleteIcon,
    LockIcon,
  },
  computed: {
    durationOptions() {
      return [
        { value: 0, label: 'None' },
        { value: 1, label: '1 mo' },
        { value: 2, label: '2 mo' },
        { value: 3, label: '3 mo' },
        { value: 4, label: '4 mo' },
        { value: 5, label: '5 mo' },
        { value: 6, label: '6 mo' },
        { value: 7, label: '7 mo' },
        { value: 8, label: '8 mo' },
        { value: 9, label: '9 mo' },
        { value: 10, label: '10 mo' },
        { value: 11, label: '11 mo' },
        { value: 12, label: '12 mo' }
      ];
    },
    ticketItems() {
      return this.modelValue.ticketItems || [];
    },
    displayItems() {
      if (this.createMode) {
        return [...this.ticketItems, { productId: null, durationMonths: null, quantity: null, amountCents: null }];
      }
      return this.ticketItems;
    },
    amountTotal() {
      return this.ticketItems.reduce((sum, item) => {
        return sum + (item.amountCents || 0);
      }, 0);
    },
    paymentPlanAmount() {
      const initialPayment = this.modelValue.amountDueToday || 0;
      return Math.max(0, this.amountTotal - initialPayment);
    },
    paymentPlanOptions() {
      const options = [];
      const planAmount = this.paymentPlanAmount;
      for (let months = 1; months <= 12; months++) {
        const monthlyAmount = planAmount / months;
        const monthlyFormatted = this.formatCurrency(monthlyAmount);
        const label = `${months} ${months === 1 ? 'month' : 'months'} (${monthlyFormatted}/month)`;
        options.push({ value: months, label });
      }
      return options;
    },
    ticketStatus() {
      return calculateTicketStatus(this.modelValue);
    },
    generatedReceivables() {
      const receivables = [];
      const amountDueToday = this.modelValue.amountDueToday || 0;
      const paymentPlanMonths = this.modelValue.paymentPlanMonths || 0;
      const dateTicketStart = this.modelValue.dateTicketStart;

      if (!dateTicketStart) {
        return receivables;
      }

      if (amountDueToday > 0) {
        receivables.push({
          dateDue: dateTicketStart,
          amountCents: amountDueToday
        });
      }

      if (this.paymentPlanAmount > 0 && paymentPlanMonths > 0) {
        const monthlyAmount = Math.floor(this.paymentPlanAmount / paymentPlanMonths);
        let amountLeft = this.paymentPlanAmount;

        for (let i = 1; i <= paymentPlanMonths; i++) {
          const isLast = (i === paymentPlanMonths);
          const amount = isLast ? amountLeft : monthlyAmount;

          const dueDate = this.calculateDueDateForMonth(dateTicketStart, i);

          receivables.push({
            dateDue: dueDate,
            amountCents: amount
          });

          amountLeft -= amount;
        }
      }

      return receivables;
    }
  },
  methods: {
    updateField(fieldName, value) {
      this.$emit('update:modelValue', {
        ...this.modelValue,
        [fieldName]: value
      });
    },
    handleInitialPaymentChange(value) {
      this.updateField('amountDueToday', value);
    },
    handlePaymentPlanAmountChange(value) {
      const initialPayment = Math.max(0, this.amountTotal - value);
      this.updateField('amountDueToday', initialPayment);
    },
    formatDate(date) {
      return formatUtils.formatDate(date);
    },
    formatCurrency(cents) {
      if (cents === null || cents === undefined) return 'N/A';
      const dollars = cents / 100;
      return '$' + dollars.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },
    formatPaymentPlan(months) {
      if (!months) return 'N/A';
      const monthlyAmount = this.paymentPlanAmount / months;
      const monthlyFormatted = this.formatCurrency(monthlyAmount);
      return `${months} ${months === 1 ? 'month' : 'months'} (${monthlyFormatted}/month)`;
    },
    calculateDueDateForMonth(startDate, monthOffset) {
      return addMonths(startDate, monthOffset);
    },
    handleProductSelect(index, productId) {
      if (index === this.ticketItems.length) {
        this.updateItem(index, 'productId', productId);
      } else {
        this.updateItem(index, 'productId', productId);
      }
    },
    async handleProductSelectEdit(index, productId) {
      // In edit mode, change the product but keep the original amount
      let newItems = [...this.ticketItems];
      const originalAmount = newItems[index].amountCents;

      try {
        const product = await queryClient.fetchQuery({
          queryKey: ['product', productId],
          queryFn: () => api.fetchProductById(productId)
        });

        newItems[index] = {
          ...newItems[index],
          productId: productId,
          productName: product.name,
          productType: product.productType,
          // In edit mode, keep the original quantity and duration unless they want to change them
          quantity: newItems[index].quantity || product.defaultQuantity || 1,
          durationMonths: newItems[index].durationMonths || product.defaultDurationMonths || 1,
          // Keep original amount - cannot be changed in edit mode
          amountCents: originalAmount
        };
      } catch (error) {
        console.error('Failed to fetch product:', error);
        newItems[index] = {
          ...newItems[index],
          productId: productId
        };
      }

      this.updateField('ticketItems', newItems);
    },
    updateItemSimple(index, field, value) {
      // Simple update without recalculating amount (for edit mode)
      let newItems = [...this.ticketItems];
      newItems[index] = {
        ...newItems[index],
        [field]: value
      };
      this.updateField('ticketItems', newItems);
    },
    removeItem(index) {
      const newItems = this.ticketItems.filter((_, i) => i !== index);
      this.updateField('ticketItems', newItems);
    },
    async updateItem(index, field, value) {
      let newItems = [...this.ticketItems];

      if (index === newItems.length) {
        newItems.push({
          productId: null,
          durationMonths: null,
          quantity: null,
          amountCents: null
        });
      }

      if (field === 'productId' && value) {
        try {
          const product = await queryClient.fetchQuery({
            queryKey: ['product', value],
            queryFn: () => api.fetchProductById(value)
          });

          newItems[index] = {
            ...newItems[index],
            productId: value,
            productName: product.name,
            productType: product.productType,
            quantity: product.defaultQuantity ?? 1,
            durationMonths: product.defaultDurationMonths ?? 1,
            amountCents: (product.defaultQuantity ?? 1) * (product.defaultUnitPriceCents ?? 0),
            defaultUnitPriceCents: product.defaultUnitPriceCents
          };
        } catch (error) {
          console.error('Failed to fetch product defaults:', error);
          newItems[index] = {
            ...newItems[index],
            [field]: value
          };
        }
      } else if (field === 'quantity') {
        const item = newItems[index];
        newItems[index] = {
          ...item,
          quantity: value,
          amountCents: item.defaultUnitPriceCents ? value * item.defaultUnitPriceCents : item.amountCents
        };
      } else {
        newItems[index] = {
          ...newItems[index],
          [field]: value
        };
      }

      this.updateField('ticketItems', newItems);
    },
    getPatientInitials() {
      const firstName = this.modelValue.patientFirstName || '';
      const lastName = this.modelValue.patientLastName || '';
      return `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase() || 'U';
    },
    getPatientName() {
      const firstName = this.modelValue.patientFirstName || '';
      const lastName = this.modelValue.patientLastName || '';
      return `${firstName} ${lastName}`.trim() || 'Unknown';
    },
    getCounselorName() {
      return this.modelValue.counselorName?.trim() || 'Unknown';
    },
    getProductName(item) {
      return item.productName?.trim() || 'Unknown';
    },
    formatDuration(months) {
      if (!months || months === 0) return 'None';
      return `${months} mo`;
    },
    getItemId(item, index) {
      return `${index}_${item.productId}`
    }
  }
}
</script>

<style scoped>
.card-ticket-form-wrapper {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.card-ticket-form {
  background: #FFF;
}

.card-header {
  padding: 16px;
  background-color: var(--content-header-bg);
  border-bottom: var(--content-header-border);
}

.card-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #262626;
}

.card-body {
  padding: 16px;
}

.form-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-row {
  display: flex;
  gap: 16px;
  align-items: start;
}

.form-label {
  font-weight: 500;
  color: #333;
  line-height: 31px;
  flex-basis: 200px;
  flex-shrink: 0;
}

.form-field {
  width: 1px;
  flex: 1;
}

.form-editor {
  max-width: 400px;
}

.form-display {
  max-width: 400px;
  color: #333;
  height: 31px;
  display: flex;
  align-items: center;
  padding: 0 2px;
}

.no-data {
  color: #999;
  font-style: italic;
}

.ticket-items-container {
  max-width: 100%;
  margin-bottom: 12px;
  border: 1px solid #e0e0e0;
  border-radius: var(--border-radius);
  overflow: hidden;
}

.items-table {
  border-collapse: collapse;
}

.items-table th {
  text-align: left;
  font-weight: 600;
  font-size: 13px;
  color: #555;
  padding: 8px;
  border-bottom: 1px solid #e0e0e0;
  background: #f9fafb;
  height: 48px;
}

.items-table td {
  padding: 8px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: middle;
}

.items-table tbody tr:last-child td {
  border-bottom: none;
}

.items-table td span {
  font-size: 14px;
  color: #333;
}

.col-product {
  width: 400px;
}

.col-duration {
  width: 150px;
}

.col-quantity {
  width: 150px;
}

.col-price {
  width: 150px;
}

.col-action {
  width: 40px;
  text-align: center;
}

.icon-btn {
  padding: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 31px;
  height: 31px;
}

.button.primary {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.entity-link {
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  color: inherit;
  transition: opacity 0.2s;
}

.entity-link:hover {
  opacity: 0.7;
}

.entity-display {
  display: flex;
  align-items: center;
  gap: 8px;
}

.entity-name {
  line-height: 31px;
}

.balance-value {
  font-weight: 600;
}

.items-table tfoot {
  background: #f9fafb;
  border-top: 1px solid #e0e0e0;
}

.total-row td {
  padding: 12px 8px;
  font-weight: 600;
  border-bottom: none !important;
}

.total-label {
  text-align: right;
  color: #333;
  font-size: 14px;
}

.total-amount {
  color: #333;
  font-size: 14px;
}

.locked-field {
  display: flex;
  align-items: center;
  gap: 6px;
  background-color: #f5f5f5;
  padding: 6px 10px;
  border: 1px solid #e0e0e0;
  color: #666;
  cursor: not-allowed;
}

.lock-icon {
  color: #999;
}
</style>
